import React from "react";
import { Minus, Maximize2, GripVertical, AlertCircle, Unplug } from "lucide-react";
import { cn } from "../../../lib/utils";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { CHANNELS, formatValue } from "./reportsCatalog";
import { getChannelIcon } from "../../channel-cats/ChannelCategories";

const TYPE_LABEL = { kpi: "KPI", breakdown: "Breakdown", table: "Table", timeseries: "Trend" };

function EmptyState() {
  return <div className="flex h-20 items-center justify-center text-xs text-muted-foreground">No data</div>;
}

function KpiTile({ widget }) {
  return (
    <div className="flex h-full min-h-[56px] flex-col justify-center">
      <span className="text-3xl font-bold tracking-tight text-foreground">
        {formatValue(widget.data && widget.data.value, widget.unit, { compact: true })}
      </span>
    </div>
  );
}

function BreakdownChart({ widget }) {
  const items = (widget.data && widget.data.items) || [];
  if (!items.length) return <EmptyState />;
  const max = Math.max(...items.map((i) => Number(i.value) || 0), 0) || 1;
  return (
    <ul className="space-y-2.5">
      {items.map((it, idx) => (
        <li key={idx}>
          <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
            <span className="truncate text-muted-foreground">{it.label || "\u2014"}</span>
            <span className="shrink-0 font-semibold text-foreground">
              {formatValue(it.value, widget.unit, { compact: true })}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-violet-600"
              style={{ width: `${Math.max(2, ((Number(it.value) || 0) / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function DataTable({ widget }) {
  const cols = (widget.data && widget.data.columns) || [];
  const rows = (widget.data && widget.data.rows) || [];
  if (!cols.length || !rows.length) return <EmptyState />;
  return (
    <div className="-mx-1 max-h-72 overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-border">
            {cols.map((c) => (
              <th
                key={c.key}
                className={cn(
                  "px-2 py-1.5 font-semibold text-muted-foreground",
                  c.align === "right" ? "text-right" : "text-left"
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="border-b border-border/40">
              {cols.map((c) => {
                const v = r[c.key];
                const text = typeof v === "number" ? formatValue(v, c.unit, { compact: false }) : v == null ? "" : String(v);
                return (
                  <td
                    key={c.key}
                    className={cn("px-2 py-1.5 text-foreground", c.align === "right" ? "text-right" : "text-left")}
                  >
                    {text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TimeSeriesChart({ widget }) {
  const series = (widget.data && widget.data.series) || [];
  const withPoints = series.filter((s) => Array.isArray(s.points) && s.points.length);
  if (!withPoints.length) return <EmptyState />;

  const W = 320;
  const H = 120;
  const pad = 6;
  const all = withPoints.flatMap((s) => s.points.map((p) => Number(p.y) || 0));
  let min = Math.min(...all);
  let max = Math.max(...all);
  if (min > 0) min = 0;
  if (min === max) max = min + 1;
  const colors = ["#6C63FF", "#22B8CF", "#F59E0B", "#EF4444"];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-32 w-full">
      {withPoints.map((s, si) => {
        const pts = s.points;
        const stepX = pts.length <= 1 ? 0 : (W - pad * 2) / (pts.length - 1);
        const path = pts
          .map((p, i) => {
            const x = pad + i * stepX;
            const y = H - pad - (((Number(p.y) || 0) - min) / (max - min)) * (H - pad * 2);
            return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
          })
          .join(" ");
        return <path key={si} d={path} fill="none" stroke={colors[si % colors.length]} strokeWidth="2" />;
      })}
    </svg>
  );
}

function WidgetBody({ widget }) {
  if (widget.status === "loading") {
    return (
      <div className="flex h-24 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (widget.status === "error") {
    if (widget.disconnected) {
      const channelLabel = CHANNELS[widget.channel]?.label || widget.channel;
      return (
        <div className="flex h-24 flex-col items-center justify-center gap-1 px-3 text-center">
          <Unplug className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">{channelLabel} not connected</span>
          <span className="text-[11px] leading-tight text-muted-foreground">
            Reconnect the channel to load this metric.
          </span>
        </div>
      );
    }
    return (
      <div className="flex h-24 items-center justify-center gap-2 px-2 text-center text-xs text-destructive">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="truncate">{widget.error || "Failed to load"}</span>
      </div>
    );
  }
  switch (widget.type) {
    case "kpi":
      return <KpiTile widget={widget} />;
    case "breakdown":
      return <BreakdownChart widget={widget} />;
    case "table":
      return <DataTable widget={widget} />;
    case "timeseries":
      return <TimeSeriesChart widget={widget} />;
    default:
      return <EmptyState />;
  }
}

export function WidgetCard({ widget, dragging, onRemove, onResize, onDragStartHandle, onDragOver, onDrop }) {
  const channel = CHANNELS[widget.channel];
  const channelIcon = getChannelIcon(widget.channel);
  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-opacity",
        dragging && "opacity-40"
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{widget.label}</p>
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            {channelIcon && <span className="text-sm leading-none">{channelIcon}</span>}
            <span className="truncate">
              {channel ? channel.label : widget.channel} &middot; {TYPE_LABEL[widget.type] || widget.type}
            </span>
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-0.5 text-muted-foreground">
          <button
            type="button"
            onClick={onResize}
            title="Resize"
            aria-label="Resize widget"
            className="rounded p-1 hover:bg-accent hover:text-foreground"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            title="Remove"
            aria-label="Remove widget"
            className="rounded p-1 hover:bg-destructive/10 hover:text-destructive"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span
            draggable
            onDragStart={onDragStartHandle}
            title="Drag to reorder"
            aria-label="Drag to reorder"
            className="cursor-grab rounded p-1 hover:bg-accent hover:text-foreground active:cursor-grabbing"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </span>
        </div>
      </header>
      <div className="flex-1 p-3">
        <WidgetBody widget={widget} />
      </div>
    </div>
  );
}
